import type { Invoice, InvoiceLineItem } from 'src/redux/billing/billing.types';
import type { Company } from 'src/pages/billing/types/billing.types';
import type { Campaign } from 'src/pages/client-management/types/client-management.types';
import { getCompanyName } from './utils';

function escapeCsvField(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Line items included in the billing line-item CSV: visible in the drawer (not hidden) and
 * marked active on either invoice_line_item_status or status (case-insensitive).
 */
export function isInvoiceLineItemEligibleForLineCsv(lineItem: InvoiceLineItem): boolean {
  if (lineItem.hide) return false;
  const li = lineItem.invoice_line_item_status?.toLowerCase() ?? '';
  const st = lineItem.status?.toLowerCase() ?? '';
  return li === 'active' || st === 'active';
}

function lineImpressions(lineItem: InvoiceLineItem): number {
  return Number(lineItem.details?.impressions ?? lineItem.impressions ?? 0);
}

function lineSpend(lineItem: InvoiceLineItem): number {
  return Number(lineItem.advertiser_spend_override ?? lineItem.advertiser_spend ?? 0);
}

function campaignAccountLabel(
  campaign: Campaign | undefined,
  invoice: Invoice,
  companyLookup: Record<string, Company>
): string {
  const fromCampaign = campaign?.account?.title?.trim();
  if (fromCampaign) return fromCampaign;
  return getCompanyName(invoice.company, companyLookup, [invoice]);
}

function campaignLabel(campaign: Campaign | undefined, lineItem: InvoiceLineItem): string {
  const title = campaign?.title?.trim();
  if (title) return title;
  return lineItem.name?.trim() ?? '';
}

/** Matches drawer description label: platform + channel */
function formatSourcePlatform(lineItem: InvoiceLineItem): string {
  const p = lineItem.platform?.replace(/_/g, ' ').trim() ?? '';
  const c = lineItem.channel?.replace(/_/g, ' ').trim() ?? '';
  if (p && c) return `${p} (${c})`;
  if (p) return p;
  if (c) return c;
  return '';
}

function compareLocale(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

/**
 * Active line items as CSV: invoices ordered by company then id; line rows sorted by account then
 * campaign within each invoice. Company name is the first column on every data row.
 */
export function buildActiveInvoiceLineItemsCsv(
  invoices: Invoice[],
  companyLookup: Record<string, Company>,
  campaigns: Campaign[]
): string {
  const campaignById = new Map(campaigns.map((c) => [c.id, c]));

  const columnHeaders = [
    'Company',
    'Account',
    'Campaign',
    'Source/Platform',
    'Impressions',
    'Spend',
  ];

  const sortedInvoices = [...invoices].sort((a, b) => {
    const ca = getCompanyName(a.company, companyLookup, [a]);
    const cb = getCompanyName(b.company, companyLookup, [b]);
    const c = compareLocale(ca, cb);
    if (c !== 0) return c;
    return compareLocale(a.id, b.id);
  });

  const lines: string[] = [columnHeaders.map(escapeCsvField).join(',')];

  for (const invoice of sortedInvoices) {
    const eligibleItems = invoice.invoice_line_items.filter((li) =>
      isInvoiceLineItemEligibleForLineCsv(li)
    );
    if (eligibleItems.length === 0) continue;

    const sortedItems = [...eligibleItems].sort((a, b) => {
      const ca = campaignById.get(a.campaign);
      const cb = campaignById.get(b.campaign);
      const accCmp = compareLocale(
        campaignAccountLabel(ca, invoice, companyLookup),
        campaignAccountLabel(cb, invoice, companyLookup)
      );
      if (accCmp !== 0) return accCmp;
      return compareLocale(campaignLabel(ca, a), campaignLabel(cb, b));
    });

    const companyName = getCompanyName(invoice.company, companyLookup, [invoice]);

    for (const lineItem of sortedItems) {
      const campaign = lineItem.campaign ? campaignById.get(lineItem.campaign) : undefined;
      lines.push(
        [
          companyName,
          campaignAccountLabel(campaign, invoice, companyLookup),
          campaignLabel(campaign, lineItem),
          formatSourcePlatform(lineItem),
          lineImpressions(lineItem),
          lineSpend(lineItem),
        ].map(escapeCsvField).join(',')
      );
    }
  }

  return lines.join('\r\n');
}

export function downloadCsvFile(content: string, filename: string): void {
  const blob = new Blob(['\ufeff', content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
